import React, { FC, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import {
  Alert,
  VisaIcon,
  AmericanExpressIcon,
  MastercardIcon,
  DiscoverIcon,
  DinersIcon,
} from '@clearcover/atlas';
import { useFormikContext } from 'formik';
import { PaymentDetailsType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { PurchaseFormValuesType } from '../../types';
import { AdyenForm } from './AdyenForm';
import styles from './styles.module.css';
import { usePurchaseContext, useAppContext } from 'QuoteApp/services/providers';

export const PaymentMethods: FC<{
  paymentDetails: PaymentDetailsType;
  embedded?: boolean;
}> = ({ paymentDetails, embedded = false }) => {
  const { errors, submitBind } = usePurchaseContext();
  const { config } = useAppContext();
  const { values } = useFormikContext<PurchaseFormValuesType>();
  const isSweepPurchase = !!config?.sweepEnabled;

  // Use a ref so the guard reads current state, not stale closure values.
  // AdyenForm's onSubmit callback captures onPurchaseSubmit at initialization
  // and never updates it — so formValues passed by Adyen have stale
  // sweepAffirmationAccepted. The ref gives us the live value.
  const sweepAffirmationRef = useRef(values.sweepAffirmationAccepted);
  useEffect(() => {
    sweepAffirmationRef.current = values.sweepAffirmationAccepted;
  }, [values.sweepAffirmationAccepted]);

  const guardedSubmitBind = useCallback(
    (formValues: PurchaseFormValuesType, paymentToken: string) => {
      if (
        isSweepPurchase &&
        formValues.autopayEnabled &&
        !sweepAffirmationRef.current
      ) {
        return;
      }
      submitBind(formValues, paymentToken);
    },
    [submitBind, isSweepPurchase]
  );
  const method = paymentDetails.paymentMethods[0];
  return (
    <div className={embedded ? undefined : styles.method}>
      <div className={embedded ? undefined : styles.borderContainer}>
        {!embedded && (
          <div className={styles.methodHeader}>
            <div
              className={classNames(styles.labelHeader, styles.singleMethod)}
            >
              <div className={styles.creditCardHeading}>
                Credit/Debit Card
                <div className={styles.creditCardIcons}>
                  <VisaIcon height={32} width={32} viewBox="0 -1 24 24" />
                  <AmericanExpressIcon
                    height={32}
                    width={32}
                    viewBox="0 -1 24 24"
                  />
                  <MastercardIcon height={32} width={32} />
                  <DiscoverIcon height={32} width={32} viewBox="0 1 24 24" />
                  <DinersIcon height={24} width={24} viewBox="0 -1 24 24" />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.creditCardForm}>
          {errors && (
            <div
              data-testid="payment-error-notification"
              className={styles.errorMessage}
            >
              <Alert message={errors.join(', ')} />
            </div>
          )}
          <div className="fs-mask" data-testid="adyen-form">
            <AdyenForm
              config={method.configuration}
              onPurchaseSubmit={guardedSubmitBind}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
